import { db } from "./dbClient";

// ─── EDDPage.jsx ──────────────────────────────────────────────────────────────
// Enhanced Due Diligence — yearly review workflow for high-risk customers
//  • Auto-pulls high-risk customers from sanctions_screening (risk_score >= 70)
//  • Manual add supported
//  • Assign review to any team member (or yourself) → writes to assignments table,
//    sends notification, appears on their Dashboard "My Open Assignments"
//  • Draft auto-save: "Save Progress" upserts draft at any time (no decision needed)
//  • Final "Complete Review" requires a decision and marks status = "completed"
//  • On login, draft is restored from DB — nothing is lost
//  • All queries scoped by company_id — no cross-company data leakage
//    (RLS policies on edd_customers + edd_reviews enforce this at DB level too)

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from 'react-i18next';
import { useTheme } from "./theme";
import ExternalResearch from "./ExternalResearch";
import { useAuditLog } from "./useAuditLog";
import { useToast, ToastContainer } from "./Toast";

const PROXY = import.meta.env.VITE_PROXY_URL || "http://127.0.0.1:3001";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}

function reviewStatus(lastReviewDate, T, tFn) {
  const days = daysSince(lastReviewDate);
  const label = (key, params) => tFn ? tFn(key, params) : params ? key : key;
  if (days === null) return { label: tFn ? tFn('edd.reviewStatus.neverReviewed') : "Never Reviewed", color: T.danger, overdue: true };
  if (days > 365)    return { label: tFn ? tFn('edd.reviewStatus.overdue', { months: Math.floor(days / 30) }) : `${Math.floor(days / 30)}mo overdue`, color: T.danger, overdue: true };
  if (days > 300)    return { label: tFn ? tFn('edd.reviewStatus.dueSoon') : "Review Due Soon", color: T.warn, overdue: false };
  return { label: tFn ? tFn('edd.reviewStatus.reviewedAgo', { days }) : `Reviewed ${days}d ago`, color: T.success, overdue: false };
}

function riskColor(r, T) {
  if (!r) return T.textDim;
  const l = r.toLowerCase();
  if (l === "high" || l === "critical") return T.danger;
  if (l === "medium")                   return T.warn;
  return T.success;
}

function getAccountNumber(cust) {
  if (!cust) return null;
  if (cust.account_number) return cust.account_number;
  if (!cust.notes) return null;
  const match = cust.notes.match(/Account Number:\s*([0-9]+)/);
  return match ? match[1] : null;
}

function Badge({ label, color, bg }) {
  return (
    <span style={{
      fontSize: 10, padding: "2px 8px", borderRadius: 4,
      fontFamily: "'DM Mono',monospace", fontWeight: 600,
      background: bg || `${color}18`, color,
    }}>{label}</span>
  );
}

function Btn({ children, onClick, variant = "default", disabled, small, style: extraStyle = {} }) {
  const C = useTheme();
  const base = {
    padding: small ? "5px 12px" : "8px 18px",
    borderRadius: 7, cursor: disabled ? "not-allowed" : "pointer",
    fontSize: small ? 11 : 13, fontFamily: "'DM Mono',monospace",
    fontWeight: 600, transition: "opacity 0.15s", opacity: disabled ? 0.5 : 1,
    border: "none",
  };
  const variants = {
    default: { background: C.accentGlow,     color: C.accent,  border: `1px solid ${C.accent}40`  },
    danger:  { background: `${C.danger}15`,  color: C.danger,  border: `1px solid ${C.danger}40`  },
    success: { background: `${C.success}15`, color: C.success, border: `1px solid ${C.success}40` },
    ghost:   { background: "transparent",    color: C.textMid, border: `1px solid ${C.border}`    },
    solid:   { background: C.accent,         color: C.bg,      border: "none"                     },
    warn:    { background: `${C.warn}15`,    color: C.warn,    border: `1px solid ${C.warn}40`    },
  };
  return (
    <button style={{ ...base, ...variants[variant], ...extraStyle }} onClick={onClick} disabled={disabled}>
      {children}
    </button>
  );
}

// ─── Add Customer Modal ───────────────────────────────────────────────────────
function AddCustomerModal({ onClose, onAdd, kycCustomers = [] }) {
  const C = useTheme();
  const { t } = useTranslation();
  const inputStyle = {
    width: "100%", background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 7, color: C.text, padding: "8px 10px",
    fontSize: 13, fontFamily: "'DM Mono',monospace", boxSizing: "border-box",
  };
  const [useKycCustomer, setUseKycCustomer] = useState(false);
  const [selectedKycCustomer, setSelectedKycCustomer] = useState(null);
  const [form, setForm] = useState({
    name: "", type: "Individual", country: "", nationality: "",
    dob: "", occupation: "", source_of_funds: "",
    pep: "No", sanctions_hit: "No", notes: "", account_number: "",
  });
  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  // When a KYC customer is selected, populate the form
  const handleKycCustomerSelect = (customer) => {
    setSelectedKycCustomer(customer);
    // Prioritize business_name for display
    let displayName = "";
    if (customer.business_name && customer.business_name.trim()) {
      displayName = customer.business_name;
    } else if (customer.full_name && customer.full_name.trim()) {
      displayName = customer.full_name;
    } else {
      displayName = [customer.first_name, customer.last_name].filter(Boolean).join(" ");
    }
    setForm({
      name: displayName,
      type: customer.type === "business" ? "Business" : "Individual",
      country: customer.country || customer.incorporation_country || "",
      nationality: customer.nationality || "",
      dob: customer.dob || "",
      occupation: customer.job_title || customer.business_type || "",
      source_of_funds: customer.source_of_funds || "",
      pep: customer.pep_declaration === true ? "Yes – Domestic PEP" : customer.pep_declaration === false ? "No" : "No",
      sanctions_hit: customer.sanctions_declaration === true ? "Confirmed Hit" : "No",
      notes: customer.notes || "",
      account_number: customer.account_number || "",
    });
  };

  const Field = ({ label, fkey, type = "text", opts }) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ color: C.textMid, fontSize: 11, marginBottom: 5 }}>{label.toUpperCase()}</div>
      {opts ? (
        <select value={form[fkey]} onChange={e => set(fkey, e.target.value)} style={inputStyle}>
          {opts.map(o => <option key={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} value={form[fkey]} onChange={e => set(fkey, e.target.value)} style={inputStyle} />
      )}
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: "28px 32px", width: 540, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 18, fontWeight: 700, marginBottom: 22 }}>{t('edd.addHighRiskCustomer')}</div>

        {/* Toggle between select from KYC or manual entry */}
        <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: C.text, fontSize: 13 }}>
            <input type="radio" checked={!useKycCustomer} onChange={() => { setUseKycCustomer(false); setSelectedKycCustomer(null); }} />
            Manually Enter
          </label>
          {kycCustomers.length > 0 && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", color: C.text, fontSize: 13 }}>
              <input type="radio" checked={useKycCustomer} onChange={() => setUseKycCustomer(true)} />
              Select from Customers ({kycCustomers.length})
            </label>
          )}
        </div>

        {useKycCustomer && kycCustomers.length > 0 ? (
          // KYC CUSTOMER SELECTION MODE
          <div style={{ marginBottom: 20 }}>
            <div style={{ color: C.textMid, fontSize: 11, marginBottom: 8 }}>CUSTOMER</div>
            <select
              value={selectedKycCustomer?.id || ""}
              onChange={e => {
                const cust = kycCustomers.find(c => c.id === e.target.value);
                if (cust) handleKycCustomerSelect(cust);
              }}
              style={inputStyle}
            >
              <option value="">-- Select a customer --</option>
              {kycCustomers.map(c => {
                // Prioritize business_name for display
                let displayName = "Customer";
                if (c.business_name && c.business_name.trim()) {
                  displayName = c.business_name;
                } else if (c.full_name && c.full_name.trim()) {
                  displayName = c.full_name;
                } else {
                  const personalName = [c.first_name, c.last_name].filter(Boolean).join(" ");
                  displayName = personalName || "Customer";
                }
                return <option key={c.id} value={c.id}>{displayName}</option>;
              })}
            </select>
          </div>
        ) : null}

        {/* FORM FIELDS */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 18px" }}>
          <div style={{ gridColumn: "1/-1" }}><Field label={t('edd.fields.name')} fkey="name" /></div>
          <Field label={t('edd.fields.type')}           fkey="type"            opts={["Individual","Business","Trust","Foundation"]} />
          <Field label={t('edd.fields.country')}        fkey="country" />
          <Field label={t('edd.fields.nationality')}    fkey="nationality" />
          <Field label={t('edd.fields.dob')}            fkey="dob"             type="date" />
          <Field label={t('edd.fields.occupation')}     fkey="occupation" />
          <Field label={t('edd.fields.sourceOfFunds')}  fkey="source_of_funds" />
          <div style={{ gridColumn: "1/-1" }}>
            <div style={{ color: C.textMid, fontSize: 11, marginBottom: 5 }}>ACCOUNT NUMBER</div>
            <input type="text" value={form.account_number} onChange={e => set("account_number", e.target.value)}
              placeholder="Auto-populated from customer data" style={inputStyle} />
          </div>
          <Field label={t('edd.fields.pepStatus')}      fkey="pep"             opts={["No","Yes – Domestic PEP","Yes – Foreign PEP","PEP Associate"]} />
          <Field label={t('edd.fields.sanctionsHit')}   fkey="sanctions_hit"   opts={["No","Possible Match","Confirmed Hit"]} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <div style={{ color: C.textMid, fontSize: 11, marginBottom: 5 }}>{t('edd.fields.notes').toUpperCase()}</div>
          <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={3}
            style={{ ...inputStyle, resize: "vertical" }} />
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>{t('common.cancel')}</Btn>
          <Btn variant="solid" onClick={() => { if (form.name.trim()) { onAdd(form); onClose(); } }}>{t('edd.addHighRiskCustomer')}</Btn>
        </div>
      </div>
    </div>
  );
}

// ─── EDD Review Form ──────────────────────────────────────────────────────────
function EDDReviewForm({ customer, transactions, alerts, cases, company, currentUser, team, onSaved }) {
  const C = useTheme();
  const { t } = useTranslation();
  const { log } = useAuditLog(company, currentUser);
  const { toasts, showToast, dismissToast } = useToast();
  const inputStyle = {
    width: "100%", background: C.bg, border: `1px solid ${C.border}`,
    borderRadius: 7, color: C.text, padding: "8px 10px",
    fontSize: 13, fontFamily: "'DM Mono',monospace", boxSizing: "border-box",
  };
  const SECTIONS = [
    { key: "risk_summary",   label: t('edd.sectionsLabels.risk_summary'),   placeholder: t('edd.sectionPlaceholders.risk_summary')   },
    { key: "tx_analysis",    label: t('edd.sectionsLabels.tx_analysis'),    placeholder: t('edd.sectionPlaceholders.tx_analysis')    },
    { key: "adverse_media",  label: t('edd.sectionsLabels.adverse_media'),  placeholder: t('edd.sectionPlaceholders.adverse_media')  },
    { key: "pep_sanctions",  label: t('edd.sectionsLabels.pep_sanctions'),  placeholder: t('edd.sectionPlaceholders.pep_sanctions')  },
    { key: "final_decision", label: t('edd.sectionsLabels.final_decision'), placeholder: t('edd.sectionPlaceholders.final_decision') },
  ];

  const DECISIONS = [
    { value: "maintain", label: t('edd.decisionsLabels.maintain'), color: C.danger  },
    { value: "escalate", label: t('edd.decisionsLabels.escalate'), color: C.danger  },
    { value: "de_risk",  label: t('edd.decisionsLabels.de_risk'),  color: C.warn    },
    { value: "exit",     label: t('edd.decisionsLabels.exit'),     color: C.danger  },
    { value: "rfi",      label: t('edd.decisionsLabels.rfi'),      color: C.gold    },
    { value: "clear",    label: t('edd.decisionsLabels.clear'),    color: C.success },
  ];

  const [fields,       setFields]       = useState({ risk_summary: "", tx_analysis: "", adverse_media: "", pep_sanctions: "", final_decision: "" });
  const [decision,     setDecision]     = useState("");
  const [reviewedBy,   setReviewedBy]   = useState(currentUser ? `${currentUser.firstName} ${currentUser.lastName}`.trim() : "");
  const [approvedBy,   setApprovedBy]   = useState("");
  const [assigneeId,   setAssigneeId]   = useState("");
  const [draftId,      setDraftId]      = useState(null);
  const [aiLoading,    setAiLoading]    = useState({});
  const [draftSaving,  setDraftSaving]  = useState(false);
  const [draftSaved,   setDraftSaved]   = useState(false);
  const [completing,   setCompleting]   = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [completed,    setCompleted]    = useState(false);
  const [loadingDraft,  setLoadingDraft]  = useState(true);
  const [pdfData,       setPdfData]       = useState(null);
  const [teamMembers,   setTeamMembers]   = useState([]);
  const [assignMsg,     setAssignMsg]     = useState(null); // { type: "ok"|"err", text }
  const [serproLoading, setSerproLoading] = useState(false);
  const [serproResult,  setSerproResult]  = useState(null);
  const [serproModal,   setSerproModal]   = useState({ open: false, cpf: "", name: "", dob: "" });

  // ── Restore draft or latest review on mount ────────────────────────────────
  useEffect(() => {
    if (!company?.id || !customer?.id) { setLoadingDraft(false); return; }
    (async () => {
      const { data } = await db.from("edd_reviews")
        .select("*")
        .eq("company_id", company.id)   // ← company isolation enforced here
        .eq("customer_id", customer.id)
        .order("created_at", { ascending: false })
        .limit(5).execute();

      if (data && data.length > 0) {
        // Prefer in-progress draft; fall back to latest completed
        const draft = data.find(r => r.status === "draft") || data[0];
        setFields({
          risk_summary:   draft.risk_summary   || "",
          tx_analysis:    draft.tx_analysis    || "",
          adverse_media:  draft.adverse_media  || "",
          pep_sanctions:  draft.pep_sanctions  || "",
          final_decision: draft.final_decision || "",
        });
        setDecision(draft.decision || "");
        setReviewedBy(draft.reviewed_by || reviewedBy);
        setApprovedBy(draft.approved_by || "");
        setAssigneeId(draft.assigned_to || "");
        if (draft.status === "draft") setDraftId(draft.id);
      }
      setLoadingDraft(false);
    })();
  }, [company?.id, customer?.id]);

  // ── Load all team members for this company directly from DB ───────────────
  useEffect(() => {
    if (!company?.id) return;
    db.from("company_members")
      .select("user_id, email, first_name, last_name, role, permission")
      .eq("company_id", company.id)
      .then(({ data, error }) => {
        if (error) { console.error("[EDD] team load failed:", error.message); return; }
        setTeamMembers((data || []).map(m => ({
          id:        m.user_id || m.email,
          email:     m.email,
          firstName: m.first_name || "",
          lastName:  m.last_name  || "",
          role:      m.role,
        })));
      });
  }, [company?.id]);

  // ── Save progress (draft upsert — no decision required) ───────────────────
  async function saveDraft() {
    if (!company?.id || !customer?.id) return;
    setDraftSaving(true);

    const payload = {
      company_id:    company.id,
      customer_id:   customer.id,
      customer_name: customer.name,
      ...fields,
      decision:      decision || null,
      reviewed_by:   reviewedBy,
      approved_by:   approvedBy,
      assigned_to:   assigneeId || null,
      status:        "draft",
      review_date:   new Date().toISOString(),
    };

    let error;
    if (draftId) {
      ({ error } = await db.from("edd_reviews").update(payload).eq("id", draftId));
    } else {
      const { data: newRow, error: insErr } = await db.from("edd_reviews").insert(payload).single().execute();
      error = insErr;
      if (newRow) setDraftId(newRow.id);
    }

    if (!error) {
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
      showToast("Draft saved", "success");
      // Write assignment record if someone is assigned
      if (assigneeId) await writeAssignment(assigneeId, false);
    } else {
      console.error("[EDD] draft save failed:", error.message);
      showToast("Failed to save draft — please try again", "error");
    }
    setDraftSaving(false);
  }

  // ── Complete review (requires decision, marks completed) ──────────────────
  async function completeReview() {
    if (!company?.id || !customer?.id || !decision) return;
    setCompleting(true);

    const payload = {
      company_id:    company.id,
      customer_id:   customer.id,
      customer_name: customer.name,
      ...fields,
      decision,
      reviewed_by:   reviewedBy,
      approved_by:   approvedBy,
      assigned_to:   assigneeId || null,
      status:        "completed",
      review_date:   new Date().toISOString(),
    };

    let error;
    if (draftId) {
      ({ error } = await db.from("edd_reviews").update(payload).eq("id", draftId));
    } else {
      const { data: newRow, error: insErr } = await db.from("edd_reviews").insert(payload).single().execute();
      error = insErr;
      if (newRow) setDraftId(newRow.id);
    }

    if (!error) {
      const now = new Date().toISOString();

      await log({
        action: "edd_review_completed",
        module: "edd",
        entityId: String(customer.id),
        entityName: customer.name,
        meta: {
          decision,
          reviewed_by: reviewedBy,
          approved_by: approvedBy || null,
          risk_level: customer.riskLevel || customer.risk_level || null,
          review_id: draftId || null,
        },
      });

      // Update edd_customers: case_status → closed, last_review_date → now
      const { error: custErr } = await db.from("edd_customers")
        .update({ case_status: "closed", last_review_date: now })
        .eq("id", customer.id)
        .eq("company_id", company.id).execute();
      if (custErr) console.error("[EDD] customer status update failed:", custErr.message);

      // Mark all EDD assignments for this customer as done (regardless of who assigned)
      await db.from("assignments")
        .update({ status: "done" })
        .eq("company_id", company.id)
        .eq("type", "edd")
        .eq("ref_id", String(customer.id));

      // Capture data for PDF then mark completed
      setPdfData({ fields, decision, reviewedBy, approvedBy, reviewDate: now });
      setCompleted(true);
      onSaved && onSaved(now);
    } else {
      console.error("[EDD] complete review failed:", error.message);
    }
    setCompleting(false);
  }

  // ── Submit for compliance review ─────────────────────────────────────────
  async function submitForReview() {
    if (!company?.id || !customer?.id) return;
    setSubmitting(true);
    const payload = {
      company_id:    company.id,
      customer_id:   customer.id,
      customer_name: customer.name,
      ...fields,
      decision:      decision || null,
      reviewed_by:   reviewedBy,
      approved_by:   approvedBy,
      assigned_to:   assigneeId || null,
      status:        "pending_review",
      review_date:   new Date().toISOString(),
    };
    let id = draftId;
    if (id) {
      await db.from("edd_reviews").update(payload).eq("id", id).execute();
    } else {
      const { data: newRow } = await db.from("edd_reviews").insert(payload).single().execute();
      if (newRow) { id = newRow.id; setDraftId(newRow.id); }
    }
    // Notify all compliance officers
    const { data: cos } = await db.from("company_members")
      .select("user_id")
      .eq("company_id", company.id)
      .in("permission", ["compliance_officer", "admin"]);
    for (const co of (cos || [])) {
      if (!co.user_id) continue;
      await db.from("notifications").insert({
        company_id:  company.id,
        user_id:     co.user_id,
        type:        "edd_review_pending",
        title:       `EDD Review pending approval: ${customer.name}`,
        body:        `An Enhanced Due Diligence review has been submitted for your approval. Open EDD Reviews to approve, decline, or request more info.`,
        link_module: "edd",
        read:        false,
      });
    }
    await log({
      action: "edd_submitted_for_review",
      module: "edd",
      entityId: String(customer.id),
      entityName: customer.name,
      meta: { review_id: id, submitted_by: reviewedBy },
    });
    setSubmitting(false);
  }

  // ── Write assignment + notification (idempotent) ──────────────────────────
  async function writeAssignment(memberId, sendNotification = true) {
    if (!company?.id || !memberId) return;
    setAssignMsg(null);
    try {
      const refId = String(customer.id);

      // Use limit(1) instead of maybeSingle() to avoid errors when multiple rows exist
      const { data: existingRows, error: fetchErr } = await supabase
        .from("assignments")
        .select("id")
        .eq("company_id", company.id)
        .eq("assigned_to", memberId)
        .eq("type", "edd")
        .eq("ref_id", refId)
        .limit(1);

      if (fetchErr) throw new Error("Could not check existing assignment: " + fetchErr.message);
      const existing = existingRows?.[0] || null;

      if (existing) {
        const { error: updErr } = await db.from("assignments").update({
          status:      "pending",
          ref_title:   `EDD Review: ${customer.name}`,
          assigned_by: currentUser?.id || null,
        }).eq("id", existing.id).execute();
        if (updErr) throw new Error("Re-assignment update failed: " + updErr.message);
      } else {
        const { error: insErr } = await db.from("assignments").insert({
          company_id:  company.id,
          assigned_by: currentUser?.id || null,
          assigned_to: memberId,
          type:        "edd",
          ref_id:      refId,
          ref_title:   `EDD Review: ${customer.name}`,
          status:      "pending",
        });
        if (insErr) throw new Error("Assignment insert failed: " + insErr.message);
      }

      if (sendNotification) {
        const { error: notifErr } = await db.from("notifications").insert({
          company_id:  company.id,
          user_id:     memberId,
          type:        "edd_assigned",
          title:       `EDD Review assigned: ${customer.name}`,
          body:        `You have been assigned the annual EDD review for ${customer.name}. Open the EDD Reviews page to begin.`,
          link_module: "edd",
          link_id:     refId,
          read:        false,
        });
        if (notifErr) console.warn("[EDD] notification failed:", notifErr.message);
      }

      // Best-effort: update edd_customers assignee fields
      try {
        const assigneeMember = teamMembers.find(m => m.id === memberId);
        const assigneeName = assigneeMember
          ? `${assigneeMember.firstName || ""} ${assigneeMember.lastName || ""}`.trim()
          : null;
        await db.from("edd_customers")
          .update({ assigned_to_id: memberId, assigned_to_name: assigneeName })
          .eq("id", customer.id)
          .eq("company_id", company.id).execute();
      } catch (_) { /* non-critical */ }

      setAssignMsg({ type: "ok", text: `✓ Assigned successfully${sendNotification ? " — notification sent" : ""}` });
      setTimeout(() => setAssignMsg(null), 4000);
    } catch (e) {
      console.error("[EDD] assignment write failed:", e.message);
      setAssignMsg({ type: "err", text: "Assignment failed: " + e.message });
    }
  }

  // ── AI draft ──────────────────────────────────────────────────────────────
  async function aiDraft(sectionKey) {
    setAiLoading(p => ({ ...p, [sectionKey]: true }));
    const txSummary = (transactions || []).slice(0, 20).map(tx =>
      `${tx.date || tx.created_at?.slice(0,10) || "?"}: ${tx.entity || "?"} ${tx.amount ? `$${tx.amount}` : ""} ${tx.country || ""}`
    ).join("\n");
    const alertsSummary = (alerts || []).slice(0, 10).map(a =>
      `[${a.risk || a.severity || "?"}] ${a.entity || a.type || "?"} – ${a.status || "open"}`
    ).join("\n");

    const prompts = {
      risk_summary:
        `You are a senior AML compliance officer writing an EDD review.
Customer: ${customer.name}, Type: ${customer.type || "Individual"}, Country: ${customer.country || "Unknown"}, PEP: ${customer.pep || "No"}, Sanctions: ${customer.sanctions_hit || "No"}.
Notes: ${customer.notes || "None"}.
Write a professional 3-4 paragraph risk summary narrative for this customer's annual EDD review, covering their risk classification, key risk factors, and overall risk profile.`,

      tx_analysis:
        `You are a senior AML compliance officer. Analyse the following transactions for ${customer.name} and write a professional transaction behaviour analysis for their annual EDD review.
Transactions (last 20):
${txSummary || "No transaction history available."}
Alerts: ${alertsSummary || "None"}
Write a 3-4 paragraph analysis covering: volume and frequency, counterparties and jurisdictions, any anomalies, and whether transactions are consistent with the expected profile.`,

      adverse_media:
        `You are a senior AML compliance officer writing the adverse media and OSINT section of an EDD review for ${customer.name} (${customer.type || "Individual"}, ${customer.country || "Unknown"}).
Draft a professional template for this section covering: search methodology, key findings (or lack thereof), regulatory actions, court records, negative news, and conclusions. Mark fields to be completed in [BRACKETS].`,

      pep_sanctions:
        `You are a senior AML compliance officer writing the PEP and sanctions re-screening section for ${customer.name}.
PEP Status: ${customer.pep || "No"}. Previous sanctions result: ${customer.sanctions_hit || "No"}.
Draft a professional re-screening section covering: lists screened (OFAC SDN, UN, EU, HMT, PEP databases), screening date, match results, and conclusions. Mark fields to complete in [BRACKETS].`,

      final_decision:
        `You are a senior AML compliance officer completing an EDD review for ${customer.name}.
Risk Summary: ${fields.risk_summary || "Not yet completed."}
Transaction Analysis: ${fields.tx_analysis || "Not yet completed."}
Draft a professional final risk decision section with 3 scenarios (maintain high risk, request further information, or de-risk) with supporting rationale for each. Plain text only.`,
    };

    try {
      const res = await fetch(`${PROXY}/api/ai`, {
        method: "POST",
        signal: AbortSignal.timeout(90000),
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 1200,
          system: "You are a senior AML compliance officer. Write professional, regulatory-grade compliance content. Plain text only, no markdown symbols or asterisks.",
          messages: [{ role: "user", content: prompts[sectionKey] }],
        }),
      });
      const data = await res.json();
      const text = data.content?.map(c => c.text || "").join("") || "";
      if (text) setFields(p => ({ ...p, [sectionKey]: text }));
      else showToast("AI returned an empty response", "warn");
    } catch (e) {
      console.error("[EDD AI] failed:", e);
      showToast("AI draft failed — please try again", "error");
    }
    setAiLoading(p => ({ ...p, [sectionKey]: false }));
  }


  // ── Save completed review as PDF ──────────────────────────────────────────
  function saveReportPDF(data) {
    if (!data) return;
    const { fields: f, decision: dec, reviewedBy: rb, approvedBy: ab, reviewDate: rd } = data;
    const DECISION_LABELS = {
      maintain: "Maintain – High Risk", escalate: "Escalate Risk",
      de_risk:  "De-Risk – Lower to Medium", exit: "Exit Relationship",
      rfi:      "Request Further Information", clear: "Clear – Lower to Low",
    };
    const fmt = d => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";
    const sec = (title, body) => !body?.trim() ? "" : `
      <div class="section">
        <div class="section-title">${title}</div>
        <div class="section-body">${(body || "").replace(/\n/g, "<br/>")}</div>
      </div>`;

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
<title>EDD Review — ${customer.name}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;700;800&family=DM+Mono:wght@300;400;500&display=swap');
  *{margin:0;padding:0;box-sizing:border-box}
  body{font-family:'DM Mono',monospace;background:#fff;color:#0A1628;padding:32px 40px;font-size:12px}
  .header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #0A2342;padding-bottom:18px;margin-bottom:24px}
  .logo{display:flex;align-items:center;gap:10px}
  .logo-hex{width:28px;height:28px;background:#00D4FF;clip-path:polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)}
  .logo-text{font-family:'Syne',sans-serif;font-weight:800;font-size:18px;color:#0A2342}
  .doc-title{font-family:'Syne',sans-serif;font-size:22px;font-weight:800;color:#0A2342;margin-bottom:6px}
  .doc-sub{font-size:11px;color:#4A5568;letter-spacing:0.08em}
  .meta{display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;background:#F4F7FB;border-radius:8px;padding:16px;margin-bottom:24px}
  .meta .lbl{font-size:9px;letter-spacing:0.12em;color:#8096B5;margin-bottom:3px}
  .meta .val{font-size:12px;font-weight:600;color:#0A1628}
  .decision{border:2px solid #0A2342;border-radius:8px;padding:14px 18px;margin-bottom:24px}
  .decision .lbl{font-size:9px;letter-spacing:0.12em;color:#8096B5;margin-bottom:4px}
  .decision .val{font-family:'Syne',sans-serif;font-size:16px;font-weight:700;color:#0A2342}
  .section{margin-bottom:20px;page-break-inside:avoid}
  .section-title{font-size:9px;letter-spacing:0.15em;color:#00D4FF;font-weight:700;border-bottom:1px solid #D1DCF0;padding-bottom:5px;margin-bottom:10px;text-transform:uppercase}
  .section-body{line-height:1.8;color:#1A2A40;font-size:12px}
  .signoff{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin-top:32px;border-top:1px solid #D1DCF0;padding-top:20px}
  .sig .line{border-bottom:1px solid #0A2342;margin-bottom:5px;height:28px}
  .sig .lbl{font-size:9px;letter-spacing:0.1em;color:#8096B5}
  .sig .name{font-weight:700;color:#0A1628;font-size:12px}
  .footer{margin-top:28px;border-top:1px solid #D1DCF0;padding-top:12px;display:flex;justify-content:space-between;font-size:9px;color:#8096B5}
  @media print{body{padding:20px 28px}}
</style></head><body>
  <div class="header">
    <div>
      <div class="doc-title">Enhanced Due Diligence Review</div>
      <div class="doc-sub">CONFIDENTIAL · CROSSREGGUARD · ${company?.name || ""}</div>
    </div>
    <div class="logo"><div class="logo-hex"></div><span class="logo-text">CrossRegGuard</span></div>
  </div>
  <div class="meta">
    <div><div class="lbl">CUSTOMER</div><div class="val">${customer.name}</div></div>
    <div><div class="lbl">TYPE</div><div class="val">${customer.type || "Individual"}</div></div>
    <div><div class="lbl">COUNTRY</div><div class="val">${customer.country || "—"}</div></div>
    <div><div class="lbl">PEP STATUS</div><div class="val">${customer.pep || "No"}</div></div>
    <div><div class="lbl">SANCTIONS</div><div class="val">${customer.sanctions_hit || "No"}</div></div>
    <div><div class="lbl">REVIEW DATE</div><div class="val">${fmt(rd)}</div></div>
  </div>
  <div class="decision">
    <div class="lbl">FINAL RISK DECISION</div>
    <div class="val">${DECISION_LABELS[dec] || dec || "—"}</div>
  </div>
  ${sec("1. Risk Summary & Narrative", f?.risk_summary)}
  ${sec("2. Transaction Behaviour Analysis", f?.tx_analysis)}
  ${sec("3. Adverse Media & OSINT Check", f?.adverse_media)}
  ${sec("4. PEP & Sanctions Re-Screen", f?.pep_sanctions)}
  ${sec("5. Final Risk Decision & Rationale", f?.final_decision)}
  <div class="signoff">
    <div class="sig"><div class="line"></div><div class="lbl">REVIEWED BY</div><div class="name">${rb || "—"}</div></div>
    <div class="sig"><div class="line"></div><div class="lbl">APPROVED BY (MLRO / SENIOR MANAGER)</div><div class="name">${ab || "—"}</div></div>
  </div>
  <div class="footer">
    <span>CrossRegGuard · EDD Report · CONFIDENTIAL</span>
    <span>Generated: ${fmt(new Date().toISOString())} · ID: EDD-${Date.now().toString(36).toUpperCase()}</span>
  </div>
</body></html>`;
    const w = window.open("", "_blank");
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => w.print(), 900); }
  }

  // ─────────────────────────────────────────────────────────────────────────
  if (loadingDraft) {
    return <div style={{ color: C.textDim, fontSize: 13, padding: "32px 0", textAlign: "center" }}>{t('edd.form.loadingReview')}</div>;
  }

  if (completed) {
    return (
      <div style={{ background: `${C.success}10`, border: `1px solid ${C.success}40`, borderRadius: 12, padding: "40px 28px", textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
        <div style={{ color: C.success, fontFamily: "'Syne',sans-serif", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>{t('edd.form.completedTitle')}</div>
        <div style={{ color: C.textMid, fontSize: 13, marginBottom: 24 }}>
          {t('edd.form.completedBody')}
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
          <Btn variant="solid" onClick={() => saveReportPDF(pdfData)}>
            {t('edd.form.savePDF')}
          </Btn>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* Info banner */}
      <div style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}30`, borderRadius: 10, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ color: C.accent, fontSize: 12, fontWeight: 600, marginBottom: 3 }}>{t('edd.form.eddFormTitle')}</div>
          <div style={{ color: C.textMid, fontSize: 12 }}>{t('edd.form.eddFormHint')}</div>
        </div>
        {draftId && <Badge label={t('edd.form.draftInProgress')} color={C.warn} />}
      </div>

      {/* Assignment panel */}
      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 12 }}>{t('edd.form.assignTitle')}</div>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ color: C.textMid, fontSize: 11, marginBottom: 5 }}>{t('edd.form.assignTo')}</div>
            <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} style={{ ...inputStyle, maxWidth: 340 }}>
              <option value="">{t('edd.form.unassigned')}</option>
              {teamMembers.length === 0 && (
                <option disabled>{t('edd.form.loadingTeam')}</option>
              )}
              {teamMembers.map(m => (
                <option key={m.id} value={m.id}>
                  {m.firstName} {m.lastName}{m.id === currentUser?.id ? " (me)" : ""}
                </option>
              ))}
            </select>
          </div>
          <Btn small variant="ghost" onClick={() => writeAssignment(assigneeId, true)} disabled={!assigneeId}>
            {t('edd.form.sendAssignment')}
          </Btn>
        </div>
        {assignMsg && (
          <div style={{ marginTop: 8, fontSize: 11, fontFamily: "'DM Mono', monospace",
            color: assignMsg.type === "ok" ? C.success : C.danger }}>
            {assignMsg.text}
          </div>
        )}
        {!assignMsg && assigneeId && (
          <div style={{ color: C.textDim, fontSize: 11, marginTop: 8 }}>
            {assigneeId === currentUser?.id
              ? t('edd.form.assignedToSelf')
              : t('edd.form.assignedToOther')}
          </div>
        )}
      </div>

      {/* Serpro Verification */}
      {customer?.type === "Individual" && (
        <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
          <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>🔐 Serpro Verification</div>
          <div style={{ color: C.textDim, fontSize: 11, marginBottom: 14 }}>
            Verify applicant against Brazilian government CPF registry (RFB database)
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => setSerproModal({
                open: true,
                cpf: customer.tax_id || "",
                name: [customer.first_name, customer.last_name].filter(Boolean).join(" ") || "",
                dob: customer.dob || ""
              })}
              disabled={serproLoading || !customer.tax_id}
              style={{
                padding: "9px 14px", borderRadius: 7, background: serproResult?.success ? C.success : C.primary,
                color: "white", border: "none", cursor: serproLoading ? "wait" : "pointer",
                fontSize: 12, fontFamily: "'DM Mono', monospace", fontWeight: 600,
                opacity: serproLoading || !customer.tax_id ? 0.5 : 1
              }}>
              {serproLoading ? "⏳ Verifying..." : serproResult?.success ? "✓ Re-verify" : "🔐 Verify CPF"}
            </button>
            {serproResult?.success && (
              <div style={{ fontSize: 12, color: C.success, display: "flex", alignItems: "center", gap: 8 }}>
                ✓ {serproResult.data?.nome}
              </div>
            )}
          </div>
        </div>
      )}

      {/* External Research / OSINT */}
      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t('edd.form.externalResearchTitle')}</div>
        <div style={{ color: C.textDim, fontSize: 11, marginBottom: 14 }}>
          {t('edd.form.externalResearchHint')}
        </div>
        <ExternalResearch
          subjectName={customer.name}
          subjectType={customer.type || "Individual"}
          subjectCountry={customer.country || ""}
        />
      </div>

      {/* Five EDD sections */}
      {SECTIONS.map(sec => (
        <div key={sec.key} style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{sec.label}</div>
            <Btn small variant="default" onClick={() => aiDraft(sec.key)} disabled={aiLoading[sec.key]}>
              {aiLoading[sec.key] ? t('edd.form.aiDrafting') : t('edd.form.aiDraft')}
            </Btn>
          </div>

          {sec.key === "final_decision" && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
              {DECISIONS.map(d => (
                <button key={d.value} onClick={() => setDecision(d.value)}
                  style={{
                    padding: "7px 14px", borderRadius: 7, cursor: "pointer", fontSize: 12,
                    fontFamily: "'DM Mono',monospace", fontWeight: decision === d.value ? 700 : 400,
                    background: decision === d.value ? `${d.color}20` : "transparent",
                    border: `1px solid ${decision === d.value ? d.color : C.border}`,
                    color: decision === d.value ? d.color : C.textMid,
                  }}>
                  {d.label}
                </button>
              ))}
            </div>
          )}

          <textarea
            value={fields[sec.key]}
            onChange={e => setFields(p => ({ ...p, [sec.key]: e.target.value }))}
            placeholder={sec.placeholder}
            rows={6}
            style={{ width: "100%", background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "10px 12px", fontSize: 13, fontFamily: "'DM Mono',monospace", resize: "vertical", boxSizing: "border-box", lineHeight: 1.7 }}
          />
        </div>
      ))}

      {/* Sign-off */}
      <div style={{ background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
        <div style={{ color: C.text, fontSize: 13, fontWeight: 600, marginBottom: 14 }}>{t('edd.form.signoffTitle')}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {[
            [t('edd.form.reviewedBy'), reviewedBy,  setReviewedBy],
            [t('edd.form.approvedBy'), approvedBy, setApprovedBy],
          ].map(([label, val, setter]) => (
            <div key={label}>
              <div style={{ color: C.textMid, fontSize: 11, marginBottom: 5 }}>{label.toUpperCase()}</div>
              <input value={val} onChange={e => setter(e.target.value)} style={inputStyle} />
            </div>
          ))}
        </div>
      </div>

      {/* Action row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Btn variant="ghost" onClick={saveDraft} disabled={draftSaving}>
            {draftSaving ? t('edd.form.saving') : t('edd.form.saveProgress')}
          </Btn>
          {draftSaved && <span style={{ color: C.success, fontSize: 12 }}>{t('edd.form.progressSaved')}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {currentUser?.permission === "analyst" && (
            <Btn variant="warn" onClick={submitForReview} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit for Review"}
            </Btn>
          )}
          {(currentUser?.permission === "compliance_officer" || currentUser?.permission === "admin") && (
            <>
              {!decision && <span style={{ color: C.textDim, fontSize: 11 }}>{t('edd.form.selectDecision')}</span>}
              <Btn variant="solid" onClick={completeReview} disabled={completing || !decision}>
                {completing ? t('edd.form.completing') : t('edd.form.completeReview')}
              </Btn>
            </>
          )}
        </div>
      </div>

    </div>
  );
}

// ─── Customer Detail View ─────────────────────────────────────────────────────
function CustomerDetail({ customer, company, currentUser, team, alerts, tmAlerts, onBack, onReviewSaved }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [tab,        setTab]        = useState("overview");
  const [txns,       setTxns]       = useState([]);
  const [cases,      setCases]      = useState([]);
  const [custAlerts, setCustAlerts] = useState([]);
  const [loading,    setLoading]    = useState(true);

  useEffect(() => {
    if (!company?.id) return;
    setLoading(true);
    // Use allSettled — a missing/erroring table never blocks the EDD Review tab
    Promise.allSettled([
      db.from("transactions")
        .select("id,entity,amount,type,country,flagged,created_at")
        .eq("company_id", company.id)
        .ilike("entity", `%${customer.name}%`)
        .order("created_at", { ascending: false }).limit(50),
      db.from("cases")
        .select("id,entity,type,amount,risk,status,date,created_at")
        .eq("company_id", company.id)
        .ilike("entity", `%${customer.name}%`)
        .order("created_at", { ascending: false }).limit(20),
    ]).then(([txResult, caseResult]) => {
      setTxns(txResult.status === "fulfilled" ? (txResult.value?.data || []) : []);
      setCases(caseResult.status === "fulfilled" ? (caseResult.value?.data || []) : []);
      const all = [...(alerts || []), ...(tmAlerts || [])];
      setCustAlerts(all.filter(a => (a.entity || "").toLowerCase().includes(customer.name.toLowerCase())));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [company?.id, customer.id]);

  const TABS = [
    { id: "overview",     label: t('edd.tabsOverview') },
    { id: "transactions", label: `${t('edd.tabsTransactions')}${txns.length > 0 ? ` (${txns.length})` : ""}` },
    { id: "alerts",       label: `${t('edd.tabsAlerts')}${custAlerts.length > 0 ? ` (${custAlerts.length})` : ""}` },
    { id: "cases",        label: `${t('edd.tabsCases')}${cases.length > 0 ? ` (${cases.length})` : ""}` },
    { id: "edd",          label: t('edd.tabsEDD') },
  ];

  const rs = reviewStatus(customer.last_review_date, C, t);

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <button onClick={onBack}
          style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, padding: "7px 14px", cursor: "pointer", fontSize: 13, fontFamily: "'DM Mono',monospace" }}>
          {t('edd.back')}
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <h2 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 22, fontWeight: 700, margin: 0 }}>{customer.name}</h2>
            <Badge label={t('edd.highRisk')} color={C.danger} />
            {rs.overdue && <Badge label={t('edd.reviewOverdue')} color={C.danger} bg={`${C.danger}20`} />}
          </div>
          <div style={{ color: C.textMid, fontSize: 12, marginTop: 3 }}>
            {customer.type || "Individual"} · {customer.country || "—"} · {rs.label}
          </div>
        </div>
        <Btn variant="default" onClick={() => setTab("edd")}>{t('edd.openReview')}</Btn>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: `1px solid ${C.border}` }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: "9px 16px", background: "transparent", border: "none",
              borderBottom: tab === t.id ? `2px solid ${C.accent}` : "2px solid transparent",
              color: tab === t.id ? C.accent : C.textMid, cursor: "pointer",
              fontSize: 12, fontFamily: "'DM Mono',monospace", fontWeight: tab === t.id ? 600 : 400,
              marginBottom: -1,
            }}>{t.label}</button>
        ))}
      </div>

      {loading && tab !== "edd" ? (
        <div style={{ color: C.textDim, fontSize: 13, padding: "32px 0", textAlign: "center" }}>{t('edd.loadingCustomerData')}</div>
      ) : (
        <>
          {/* OVERVIEW */}
          {tab === "overview" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
                <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", marginBottom: 14 }}>{t('edd.overviewSection1')}</div>
                {[
                  [t('edd.overviewFields.fullName'),      customer.name],
                  [t('edd.overviewFields.type'),          customer.type || "Individual"],
                  [t('edd.overviewFields.country'),       customer.country || "—"],
                  [t('edd.overviewFields.nationality'),   customer.nationality || "—"],
                  [t('edd.overviewFields.dob'),           customer.dob || "—"],
                  [t('edd.overviewFields.occupation'),    customer.occupation || "—"],
                  [t('edd.overviewFields.sourceOfFunds'), customer.source_of_funds || "—"],
                  ["Account Number",                      getAccountNumber(customer) || "—"],
                  [t('edd.overviewFields.added'),         customer.date_added ? new Date(customer.date_added).toLocaleDateString() : "—"],
                ].map(([label, val]) => (
                  <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                    <span style={{ color: C.textMid }}>{label}</span>
                    <span style={{ color: val && label === "Account Number" ? C.accent : C.text, maxWidth: "55%", textAlign: "right", fontFamily: val && label === "Account Number" ? "'DM Mono',monospace" : "inherit", fontWeight: val && label === "Account Number" ? 600 : 400 }}>{val}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
                  <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", marginBottom: 14 }}>{t('edd.overviewSection2')}</div>
                  {[
                    [t('edd.riskFields.riskClassification'), <Badge key="r" label={t('edd.highRisk')} color={C.danger} />],
                    [t('edd.riskFields.pepStatus'),          <Badge key="p" label={customer.pep || "No"} color={customer.pep && customer.pep !== "No" ? C.danger : C.success} />],
                    [t('edd.riskFields.sanctions'),          <Badge key="s" label={customer.sanctions_hit || "No"} color={customer.sanctions_hit && customer.sanctions_hit !== "No" ? C.danger : C.success} />],
                    [t('edd.riskFields.lastEDDReview'),      <span key="l" style={{ color: rs.color, fontSize: 12 }}>{rs.label}</span>],
                    [t('edd.riskFields.nextReviewDue'),      <span key="n" style={{ color: C.text, fontSize: 12 }}>{customer.last_review_date ? new Date(new Date(customer.last_review_date).getTime() + 365 * 86400000).toLocaleDateString() : t('edd.riskFields.overdue')}</span>],
                    [t('edd.riskFields.openAlerts'),         <span key="a" style={{ color: custAlerts.filter(a => !a.status || a.status === "open").length > 0 ? C.danger : C.success, fontSize: 12 }}>{custAlerts.filter(a => !a.status || a.status === "open").length}</span>],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                      <span style={{ color: C.textMid }}>{label}</span>
                      {val}
                    </div>
                  ))}
                </div>
                {customer.notes && (
                  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
                    <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.1em", marginBottom: 10 }}>{t('edd.overviewNotes')}</div>
                    <div style={{ color: C.text, fontSize: 13, lineHeight: 1.7 }}>{customer.notes}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* TRANSACTIONS */}
          {tab === "transactions" && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 16 }}>{t('edd.txHistory')}</div>
              {txns.length === 0 ? (
                <div style={{ color: C.textDim, textAlign: "center", padding: "32px 0", fontSize: 13 }}>{t('edd.noTransactions')}</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>{["Date","Entity","Amount","Type","Country","Risk","Status"].map(h => (
                      <th key={h} style={{ color: C.textDim, textAlign: "left", padding: "6px 10px", borderBottom: `1px solid ${C.border}`, fontWeight: 500 }}>{h}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {txns.map((t, i) => (
                      <tr key={t.id || i} style={{ borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: "8px 10px", color: C.textMid }}>{t.created_at?.slice(0,10) || "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.text }}>{t.entity || "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.text, fontFamily: "'DM Mono',monospace" }}>{t.amount ? `$${Number(t.amount).toLocaleString()}` : "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.textMid }}>{t.type || "—"}</td>
                        <td style={{ padding: "8px 10px", color: C.textMid }}>{t.country || "—"}</td>
                        <td style={{ padding: "8px 10px" }}><Badge label={t.flagged ? "Flagged" : "Clear"} color={t.flagged ? C.danger : C.success} /></td>
                        <td style={{ padding: "8px 10px" }}><Badge label="—" color={C.textDim} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* ALERTS */}
          {tab === "alerts" && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 16 }}>{t('edd.alertsTitle')}</div>
              {custAlerts.length === 0 ? (
                <div style={{ color: C.textDim, textAlign: "center", padding: "32px 0", fontSize: 13 }}>{t('edd.noAlerts')}</div>
              ) : custAlerts.map((a, i) => (
                <div key={a.id || i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: C.surfaceAlt, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 8 }}>
                  <Badge label={a.risk || a.severity || "?"} color={riskColor(a.risk || a.severity, C)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ color: C.text, fontSize: 13 }}>{a.entity || a.type || "Alert"}</div>
                    <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{a.type || ""} · {a.date || a.created_at?.slice(0,10) || "—"}</div>
                  </div>
                  <Badge label={a.status || "open"} color={a.status === "closed" ? C.textDim : C.danger} />
                </div>
              ))}
            </div>
          )}

          {/* CASES */}
          {tab === "cases" && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 20 }}>
              <div style={{ color: C.text, fontWeight: 600, fontSize: 14, marginBottom: 16 }}>{t('edd.casesTitle')}</div>
              {cases.length === 0 ? (
                <div style={{ color: C.textDim, textAlign: "center", padding: "32px 0", fontSize: 13 }}>{t('edd.noCases')}</div>
              ) : cases.map((c, i) => (
                <div key={c.id || i} style={{ padding: "12px 14px", background: C.surfaceAlt, borderRadius: 8, border: `1px solid ${C.border}`, marginBottom: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{c.entity || "—"}</div>
                      <div style={{ color: C.textDim, fontSize: 11, marginTop: 2 }}>{c.type || "—"} · {c.date || c.created_at?.slice(0,10) || "—"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <Badge label={c.risk || "—"} color={riskColor(c.risk, C)} />
                      <Badge label={c.status || "open"} color={c.status === "closed" ? C.textDim : C.danger} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* EDD REVIEW */}
          {tab === "edd" && (
            <EDDReviewForm
              customer={customer}
              transactions={txns}
              alerts={custAlerts}
              cases={cases}
              company={company}
              currentUser={currentUser}
              team={team}
              onSaved={(date) => onReviewSaved(customer.id, date)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── Main EDD Page ────────────────────────────────────────────────────────────
export default function EDDPage({ company, currentUser, team = [], alerts = [], tmAlerts = [] }) {
  const C = useTheme();
  const { t } = useTranslation();
  const [customers,      setCustomers]      = useState([]);
  const [kycCustomers,   setKycCustomers]   = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [selected,       setSelected]       = useState(null);
  const [showAdd,        setShowAdd]        = useState(false);
  const [search,         setSearch]         = useState("");
  const [filterStatus,   setFilterStatus]   = useState("All");

  // ── Load — strictly scoped to company_id (+ RLS at DB level) ──────────────
  const loadCustomers = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);

    // 1. Existing EDD customers for THIS company only
    const { data: eddRows } = await db.from("edd_customers")
      .select("*").eq("company_id", company.id).order("date_added", { ascending: false }).execute();

    // 2. Auto-import high-risk from sanctions_screening (risk_score >= 70 = High)
    const { data: kycRows } = await db.from("sanctions_screening")
      .select("subject_name,subject_nationality,risk_score,match_count,notes")
      .eq("company_id", company.id)
      .execute();

    const highRiskKyc = (kycRows || []).filter(r => r.risk_score >= 70);

    const existingNames = new Set((eddRows || []).map(r => (r.name || "").toLowerCase()));
    // Dedupe by subject_name — take highest risk_score per name
    const kycByName = {};
    (highRiskKyc || []).forEach(k => {
      const key = (k.subject_name || "").toLowerCase();
      if (key && (!kycByName[key] || k.risk_score > kycByName[key].risk_score)) kycByName[key] = k;
    });
    const toImport = Object.values(kycByName).filter(k => !existingNames.has((k.subject_name || "").toLowerCase()));

    if (toImport.length > 0) {
      await db.from("edd_customers").insert(toImport.map(k => ({
        company_id: company.id,
        name:       k.subject_name,
        type:       "Individual",
        risk_level: k.risk_score >= 70 ? "High" : "Medium",
        risk_score: k.risk_score,
        notes:      k.notes || `Auto-imported from Sanctions Screening (risk score: ${k.risk_score})`,
        date_added: new Date().toISOString(),
        source:     "kyc_auto",
      }))).execute();
    }

    // 3. Reload final list
    const { data: final } = await db.from("edd_customers")
      .select("*").eq("company_id", company.id).order("date_added", { ascending: false }).execute();

    // 4. Attach last completed review date per customer
    const { data: reviews } = await db.from("edd_reviews")
      .select("customer_id, review_date, status")
      .eq("company_id", company.id)
      .eq("status", "completed")
      .order("review_date", { ascending: false }).execute();

    const lastReview = {};
    (reviews || []).forEach(r => { if (!lastReview[r.customer_id]) lastReview[r.customer_id] = r.review_date; });

    setCustomers((final || []).map(c => ({ ...c, last_review_date: lastReview[c.id] || c.last_review_date || null })));
    setLoading(false);
  }, [company?.id]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  // Load approved KYC customers for the dropdown in AddCustomerModal
  useEffect(() => {
    if (!company?.id) return;
    db.from("kyc_submissions")
      .select("id, first_name, last_name, business_name, account_number, dob, nationality, email, phone, tax_id, job_title, employer_name, purpose_of_account, address_line1, address_line2, city, post_code, country, incorporation_country, business_type, website, source_of_funds, pep_declaration, sanctions_declaration, type")
      .eq("company_id", company.id)
      .eq("status", "approved")
      .order("submitted_at", { ascending: false })
      .limit(500)
      .execute()
      .then(({ data }) => {
        console.log('[EDD] Loaded', data?.length || 0, 'approved KYC customers');
        setKycCustomers(data || []);
      })
      .catch(err => {
        console.warn('[EDD] Failed to load KYC customers:', err);
        setKycCustomers([]);
      });
  }, [company?.id]);

  function handleAddCustomer(form) {
    if (!company?.id) return;
    const payload = {
      company_id: company.id,
      name: form.name || "",
      type: form.type || "Individual",
      country: form.country || "",
      nationality: form.nationality || "",
      dob: form.dob || "",
      occupation: form.occupation || "",
      source_of_funds: form.source_of_funds || "",
      account_number: form.account_number || "",
      pep: form.pep || "No",
      sanctions_hit: form.sanctions_hit || "No",
      notes: form.notes || "",
      date_added: new Date().toISOString(),
      source: "manual",
    };
    console.log('[EDD] Adding customer with payload:', payload);
    db.from("edd_customers")
      .insert(payload)
      .execute()
      .then(({ data, error }) => {
        if (error) {
          console.error('[EDD] Failed to add customer:', error);
          return;
        }
        if (data) {
          console.log('[EDD] Customer added successfully:', data);
          // data is an array from insert
          const newCustomers = Array.isArray(data) ? data : [data];
          setCustomers(prev => [...newCustomers, ...prev]);
        }
      });
  }

  function handleReviewSaved(customerId, date) {
    setCustomers(prev => prev.map(c => c.id === customerId ? { ...c, last_review_date: date } : c));
    if (selected?.id === customerId) setSelected(prev => ({ ...prev, last_review_date: date }));
  }

  // ── Inline case status update ──────────────────────────────────────────────
  async function updateCaseStatus(customerId, newStatus, e) {
    e.stopPropagation(); // prevent row click opening detail
    await db.from("edd_customers")
      .update({ case_status: newStatus })
      .eq("id", customerId)
      .eq("company_id", company.id).execute();
    setCustomers(prev => prev.map(c => c.id === customerId ? { ...c, case_status: newStatus } : c));
  }

  const overdueCount  = customers.filter(c => reviewStatus(c.last_review_date, C, t).overdue).length;
  const dueSoonCount  = customers.filter(c => { const d = daysSince(c.last_review_date); return d !== null && d > 300 && d <= 365; }).length;
  const reviewedCount = customers.filter(c => { const d = daysSince(c.last_review_date); return d !== null && d <= 300; }).length;

  const CASE_STATUS_VALS = new Set(["open","in_review","awaiting_approval","closed","never_reviewed"]);

  const filtered = customers.filter(c => {
    if (search && !(c.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    const rs = reviewStatus(c.last_review_date, C, t);
    const d  = daysSince(c.last_review_date);
    // Review cycle filters
    if (filterStatus === "Overdue"  && !rs.overdue) return false;
    if (filterStatus === "Due Soon" && (rs.overdue || d === null || d <= 300)) return false;
    if (filterStatus === "Reviewed" && (rs.overdue || d === null || d > 300)) return false;
    // Case status filters
    if (CASE_STATUS_VALS.has(filterStatus) && (c.case_status || "never_reviewed") !== filterStatus) return false;
    return true;
  });

  if (selected) {
    return (
      <CustomerDetail
        customer={selected}
        company={company}
        currentUser={currentUser}
        team={team}
        alerts={alerts}
        tmAlerts={tmAlerts}
        onBack={() => setSelected(null)}
        onReviewSaved={handleReviewSaved}
      />
    );
  }

  async function runSerproVerification() {
    if (!customer?.tax_id || !customer?.dob) {
      alert("CPF and date of birth are required for Serpro verification");
      return;
    }
    setSerproLoading(true);
    try {
      console.log("[EDD] Running Serpro verification for:", customer.tax_id);
      const response = await fetch("/api/serpro", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "cpf",
          data: { cpf: customer.tax_id, birth_date: customer.dob }
        })
      });
      const result = await response.json();
      console.log("[EDD] Serpro result:", result);
      setSerproResult(result);
      setSerproModal({ open: false, cpf: "", name: "", dob: "" });

      if (result.error) {
        alert(`Serpro verification failed: ${result.error}`);
      } else {
        alert(`✓ Serpro verified!\nName: ${result.data?.nome}\nStatus: ${result.data?.situacao?.descricao}`);
        // Add to fields for narrative
        setFields(prev => ({
          ...prev,
          risk_summary: (prev.risk_summary || "") + `\n\n[SERPRO VERIFICATION]\nGovernment CPF Status: ${result.data?.situacao?.descricao || "Unknown"}\nRegistered Name: ${result.data?.nome || "N/A"}`
        }));
      }
    } catch(e) {
      console.error("[EDD] Serpro error:", e);
      alert(`Serpro error: ${e.message}`);
    }
    setSerproLoading(false);
  }

  return (
    <div>
      {showAdd && <AddCustomerModal onClose={() => setShowAdd(false)} onAdd={handleAddCustomer} kycCustomers={kycCustomers} />}

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ color: C.text, fontFamily: "'Syne',sans-serif", fontSize: 26, fontWeight: 700, margin: 0 }}>
            {t('edd.title')}
          </h1>
          <p style={{ color: C.textMid, margin: "4px 0 0", fontSize: 14 }}>
            {t('edd.subtitle')} · {customers.length} {t('common.total').toLowerCase()}
          </p>
        </div>
        <Btn variant="solid" onClick={() => setShowAdd(true)}>{t('edd.addCustomerBtn')}</Btn>
      </div>

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 14, marginBottom: 24 }}>
        {[
          { label: t('edd.stats.totalHighRisk'), value: customers.length, color: C.danger,  sublabel: t('edd.stats.totalUnder')   },
          { label: t('edd.stats.reviewOverdue'), value: overdueCount,     color: C.danger,  sublabel: t('edd.stats.overdue12m')   },
          { label: t('edd.stats.dueSoon'),       value: dueSoonCount,     color: C.warn,    sublabel: t('edd.stats.within60')     },
          { label: t('edd.stats.reviewed'),      value: reviewedCount,    color: C.success, sublabel: t('edd.stats.upToDate')     },
        ].map(s => (
          <div key={s.label} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "16px 20px", borderTop: `2px solid ${s.color}` }}>
            <div style={{ color: C.textMid, fontSize: 11, letterSpacing: "0.08em", marginBottom: 2 }}>{s.label.toUpperCase()}</div>
            <div style={{ color: C.textDim, fontSize: 10, marginBottom: 8 }}>{s.sublabel.toUpperCase()}</div>
            <div style={{ color: s.color, fontFamily: "'Syne',sans-serif", fontSize: 30, fontWeight: 700 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search customers…"
          style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "8px 14px", fontSize: 13, fontFamily: "'DM Mono',monospace", width: 220 }} />

        <div style={{ width: 1, height: 20, background: C.border }} />

        {/* Review cycle filters */}
        {[
          { key: "All",      label: t('edd.filterAll')      },
          { key: "Overdue",  label: t('edd.filterOverdue')  },
          { key: "Due Soon", label: t('edd.filterDueSoon')  },
          { key: "Reviewed", label: t('edd.filterReviewed') },
        ].map(f => (
          <button key={f.key} onClick={() => setFilterStatus(f.key)}
            style={{
              padding: "6px 12px", borderRadius: 7, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace",
              background: filterStatus === f.key ? C.accentGlow : "transparent",
              border: `1px solid ${filterStatus === f.key ? C.accent : C.border}`,
              color: filterStatus === f.key ? C.accent : C.textMid,
            }}>{f.label}</button>
        ))}

        <div style={{ width: 1, height: 20, background: C.border }} />

        {/* Case status filters */}
        {[
          { val: "open",              label: t('edd.caseStatusLabels.open'),              color: C.danger  },
          { val: "in_review",         label: t('edd.caseStatusLabels.in_review'),         color: C.accent  },
          { val: "awaiting_approval", label: t('edd.caseStatusLabels.awaiting_approval'), color: C.warn    },
          { val: "closed",            label: t('edd.caseStatusLabels.closed'),            color: C.success },
        ].map(f => (
          <button key={f.val} onClick={() => setFilterStatus(f.val)}
            style={{
              padding: "6px 12px", borderRadius: 7, cursor: "pointer", fontSize: 11, fontFamily: "'DM Mono',monospace",
              background: filterStatus === f.val ? `${f.color}18` : "transparent",
              border: `1px solid ${filterStatus === f.val ? f.color : C.border}`,
              color: filterStatus === f.val ? f.color : C.textMid,
            }}>{f.label}</button>
        ))}

        {overdueCount > 0 && (
          <span style={{ marginLeft: "auto", background: `${C.danger}18`, color: C.danger, fontSize: 11, padding: "5px 12px", borderRadius: 6, fontFamily: "'DM Mono',monospace" }}>
            {overdueCount !== 1 ? t('edd.overdueWarningPlural', { count: overdueCount }) : t('edd.overdueWarning', { count: overdueCount })}
          </span>
        )}
      </div>

      {/* Table */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: C.surfaceAlt }}>
              {[t('edd.tableHeaders.customer'),t('edd.tableHeaders.type'),t('edd.tableHeaders.country'),t('edd.tableHeaders.pep'),t('edd.tableHeaders.sanctions'),t('edd.tableHeaders.assignedTo'),t('edd.tableHeaders.caseStatus'),t('edd.tableHeaders.lastReview'),t('edd.tableHeaders.reviewCycle'),t('edd.tableHeaders.actions')].map(h => (
                <th key={h} style={{ color: C.textDim, textAlign: "left", padding: "11px 16px", fontWeight: 500, fontSize: 11, letterSpacing: "0.06em", borderBottom: `1px solid ${C.border}` }}>
                  {h.toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} style={{ textAlign: "center", padding: "40px 0", color: C.textDim }}>{t('edd.loadingCustomers')}</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={10} style={{ textAlign: "center", padding: "40px 0", color: C.textDim }}>
                {customers.length === 0
                  ? t('edd.noCustomersYet')
                  : t('edd.noCustomersFilter')}
              </td></tr>
            ) : filtered.map(c => {
              const rs = reviewStatus(c.last_review_date, C, t);
              const caseStatus = c.case_status || "never_reviewed";
              const CASE_STATUS_CFG = {
                never_reviewed:    { label: t('edd.caseStatusLabels.never_reviewed'),    color: C.textDim  },
                open:              { label: t('edd.caseStatusLabels.open'),              color: C.danger   },
                in_review:         { label: t('edd.caseStatusLabels.in_review'),         color: C.accent   },
                awaiting_approval: { label: t('edd.caseStatusLabels.awaiting_approval'), color: C.warn     },
                closed:            { label: t('edd.caseStatusLabels.closed'),            color: C.success  },
              };
              const csCfg = CASE_STATUS_CFG[caseStatus] || CASE_STATUS_CFG.never_reviewed;
              return (
                <tr key={c.id} onClick={() => setSelected(c)}
                  style={{ borderBottom: `1px solid ${C.border}`, cursor: "pointer", transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = C.surfaceAlt}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ color: C.text, fontWeight: 600 }}>{c.name}</div>
                    <div style={{ color: C.textDim, fontSize: 10, marginTop: 2 }}>
                      {c.source === "kyc_auto" ? t('edd.autoImportedKyc') : c.source === "alert_auto" ? t('edd.autoImportedAlert') : t('edd.manualEntry')}
                    </div>
                  </td>
                  <td style={{ padding: "12px 16px", color: C.textMid }}>{c.type || "Individual"}</td>
                  <td style={{ padding: "12px 16px", color: C.textMid }}>{c.country || "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    {c.pep && c.pep !== "No" ? <Badge label={c.pep} color={C.danger} /> : <span style={{ color: C.textDim, fontSize: 12 }}>No</span>}
                  </td>
                  <td style={{ padding: "12px 16px" }}>
                    {c.sanctions_hit && c.sanctions_hit !== "No" ? <Badge label={c.sanctions_hit} color={C.danger} /> : <span style={{ color: C.textDim, fontSize: 12 }}>Clear</span>}
                  </td>

                  {/* Assigned To */}
                  <td style={{ padding: "12px 16px" }}>
                    {c.assigned_to_name ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                          background: `linear-gradient(135deg,${C.borderLight},${C.border})`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          color: C.textMid, fontSize: 9, fontWeight: 700,
                        }}>
                          {c.assigned_to_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0,2)}
                        </div>
                        <span style={{ color: C.text, fontSize: 12 }}>{c.assigned_to_name}</span>
                      </div>
                    ) : (
                      <span style={{ color: C.textDim, fontSize: 12 }}>—</span>
                    )}
                  </td>

                  {/* Case Status — inline dropdown */}
                  <td style={{ padding: "8px 16px" }} onClick={e => e.stopPropagation()}>
                    <select
                      value={caseStatus}
                      onChange={e => updateCaseStatus(c.id, e.target.value, e)}
                      style={{
                        background: `${csCfg.color}15`,
                        border: `1px solid ${csCfg.color}50`,
                        borderRadius: 6, color: csCfg.color,
                        fontSize: 11, fontFamily: "'DM Mono',monospace",
                        fontWeight: 600, padding: "4px 8px", cursor: "pointer",
                        outline: "none",
                      }}>
                      {Object.entries(CASE_STATUS_CFG).map(([val, cfg]) => (
                        <option key={val} value={val} style={{ background: C.surface, color: C.text }}>
                          {cfg.label}
                        </option>
                      ))}
                    </select>
                  </td>

                  <td style={{ padding: "12px 16px", color: C.textMid, fontSize: 12 }}>
                    {c.last_review_date ? new Date(c.last_review_date).toLocaleDateString() : "—"}
                  </td>
                  <td style={{ padding: "12px 16px" }}><Badge label={rs.label} color={rs.color} /></td>
                  <td style={{ padding: "12px 16px", textAlign: "right" }}>
                    <span style={{ color: C.accent, fontSize: 12 }}>{t('edd.openLink')}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}